import { useEffect, useMemo, useRef, useState } from 'react';
import { isNumber, isString, uniq } from 'utils';

import { constants, isEqual, isHidden, isVisible, usePreferences } from '@atlas/core';

import { marketGroupTypes } from 'common/constants';
import useTranslate from 'common/I18n/Interpreter';

const defaultGroupValue = { group: null, type: marketGroupTypes.marketGroup };

const filterGroups = group => isString(group) || isNumber(group);

const useEventMarketGroups = event => {
  const translate = useTranslate();
  const { marketTypes = {} } = event;
  const previousEventState = useRef(event);
  const prevMarketGroupsRef = useRef(null);
  const [activeGroup, setActiveGroup] = useState(defaultGroupValue);
  const { favoriteMarketTypes } = usePreferences();

  const isArchivedEvent = event.sportService === constants.SPORT_SERVICE.ARCHIVED;
  const marketGroups = useMemo(() => {
    let marketGroups = [];
    let betGroups = [];

    Object.values(marketTypes)
      .sort((a, b) => a.orderPosition - b.orderPosition)
      .forEach(({ betGroup, groups, markets }) => {
        const hasVisibleMarkets = Object.values(markets).some(isVisible);

        if (hasVisibleMarkets || isArchivedEvent) {
          marketGroups = marketGroups.concat(groups);
          betGroups.push(Number(betGroup));
        }
      });

    const filterMarketTypes = isArchivedEvent ? isHidden : isVisible;
    const hasFavoriteMarkets =
      favoriteMarketTypes.length > 0 &&
      Object.values(marketTypes)
        .filter(marketType => Object.values(marketType.markets).some(filterMarketTypes))
        .some(({ id }) => favoriteMarketTypes.includes(id));

    marketGroups = [
      hasFavoriteMarkets && {
        icon: 'star',
        value: {
          group: 'FAVORITES',
          type: 'market_group',
        },
      },
      {
        text: translate('all'),
        value: defaultGroupValue,
      },
      ...uniq(marketGroups)
        .filter(filterGroups)
        .map(group => ({
          text: translate(group.toLowerCase().replace('_', ' ')),
          value: { group, type: marketGroupTypes.marketGroup },
        }))
        .filter(({ value: { group } }) => Boolean(group)),
    ].filter(Boolean);

    betGroups = uniq(betGroups)
      .filter(filterGroups)
      .sort()
      .map(betGroup => ({
        text: `${betGroup} ${translate('bet_group')}`,
        value: { group: betGroup, type: marketGroupTypes.betGroup },
      }));

    if (betGroups.length === 1) {
      betGroups = [];
    }

    // TODO: market groups could contain filter by groups as well
    // const Groups = _.concat(marketGroups, betGroups)
    const Groups = marketGroups;

    if (!isEqual(Groups, prevMarketGroupsRef.current)) {
      prevMarketGroupsRef.current = Groups;
    }

    const isActiveGroupAvailable = Groups.some(
      ({ value: { group } }) => group === activeGroup?.group,
    );
    // if active group is not available anymore then default group should become active
    if (!isActiveGroupAvailable) {
      setActiveGroup(defaultGroupValue);
    }

    return prevMarketGroupsRef.current;
  }, [marketTypes, translate, favoriteMarketTypes]);

  useEffect(() => {
    // Redirecting from prematch to live page when event starts
    // if (
    //   isSameEvent
    //     && (previousEventState.current.sportService !== sportService.LIVE)
    //     && (event.sportService === sportService.LIVE)
    // ) {
    //   sportsbookRedirect(event, true);
    // }

    previousEventState.current = event;
  }, [event]);

  // Set active group to default if there are no currently active group in market groups
  useEffect(() => {
    if (!marketGroups.find(({ value }) => value.group === activeGroup.group)) {
      setActiveGroup(null);
    }
  }, [marketGroups, activeGroup]);

  return { activeGroup, marketGroups, setActiveGroup };
};

export default useEventMarketGroups;
